import React, { useEffect, useState } from 'react'
import { Field, FormikProvider, useFormik } from 'formik'
import * as Yup from 'yup'
import ComboboxAutocomplete from '../../../../components/Form/ComboboxAutocomplete'
import FormikToggle from '../../../../components/Form/FormikToggle'
import Button from '../../../../components/Form/Button'
import Input from '../../../../components/Form/Input'
import Badge from '../../../../components/Badge'
import Panel from '../../../../components/Panel'
import { CustomContentModal } from '../../../../components/CustomContentModal'
import {
  BillingOptionObject,
  CcFeePolicyAdmin,
  TenantAccountBillingStatusResponse,
  TenantAccountStatusResponse,
} from '../../../../apis/types'
import {
  getCcFeePolicyApi,
  getTenantAccountBillingStatusApi,
  getTenantAccountStatusApi,
  updateBillingModeApi,
  updateKingBillingConfigApi,
  updateTenantAccountBillingStatusApi,
  updateTenantAccountStatusApi,
  updateUtilityBillingConfigApi,
} from '../../../../apis/apis'
import { pushToast } from '../../../../components/Toaster/Toaster.slice'
import { useAppDispatch, useAppSelector } from '../../../../shared/redux/hooks'
import {
  ccFeeAmountLabel,
  ccFeeCapLabel,
  ccFeeChargingHint,
  ccFeeChargingStatus,
  ccFeeDateLabel,
  ccFeeModeLabel,
  ccFeePanelDescription,
  ccFeePassThroughLabel,
  ccFeeScheduleLabel,
  ccFeeSourceLabel,
} from './ccFeePolicyDisplay'

const HISTORY_PREVIEW = 5

const PanelHeader: React.FC<{ title: string; description?: string; action?: React.ReactNode }> = ({
  title,
  description,
  action,
}) => (
  <div className="flex w-full items-center justify-between gap-3">
    <div className="min-w-0">
      <div>{title}</div>
      {description != null && description !== '' && (
        <div className="mt-0.5 text-xs font-normal text-slate-600 dark:text-slate-300">{description}</div>
      )}
    </div>
    {action != null && <div className="shrink-0">{action}</div>}
  </div>
)

const EditPanelButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <Button type="button" size={1} color="slate" onClick={onClick}>
    Edit
  </Button>
)
const billingModeOptionsDescriptionMap = new Map([
  ['CONSOLIDATED', 'Consolidated'],
  ['STANDALONE', 'Standalone'],
  ['CONSOLIDATED_ESTIMATED', 'Consolidated Estimated'],
  ['STANDALONE_ESTIMATED', 'Standalone Estimated'],
  ['BALANCE_ONLY', 'Balance Only'],
])

const billingModeImplicationMap = new Map([
  ['CONSOLIDATED', 'Solar + utility charges on one King invoice'],
  ['STANDALONE', 'Solar-only invoice; tenant still receives a utility bill'],
  ['CONSOLIDATED_ESTIMATED', 'Advance / estimated consolidated bill'],
  ['STANDALONE_ESTIMATED', 'Estimated solar-only bill'],
  ['BALANCE_ONLY', 'Payment plans and balance only — no normal energy billing'],
])

const utilityBillTypeDescriptionMap = new Map([
  ['STANDARD', 'Standard bill (most utilities)'],
  ['DETAIL', 'Detailed charges for a given meter (PG&E only)'],
  ['STANDARD_AND_DETAIL', 'Standard bill & detailed bill (PG&E only)'],
  ['STANDARD_OMNIBUS', 'Standard bill for multiple locations - only one location is billed'],
  ['STANDARD_OMNIBUS_AND_DETAIL', 'Standard and detailed bill for multiple locations - only one location is billed'],
  ['NONE', 'No access to bills'],
])

interface TabBillingOptionsProps {
  title: string
  utilityFormInitialValues: {
    billType?: string
    ccaProgram?: string
    demandCreditProgram?: string
  }
  kingFormInitialValues: {
    billType?: string
    includeChart?: boolean
    includeSummary?: boolean
    energyDiscount?: number
    demandDiscount?: number
    billingMode?: string
  }
  utilityBillingOptions: BillingOptionObject[]
  kingBillingOptions: BillingOptionObject[]
  enrollmentOptions: BillingOptionObject[]
  billingModeOptions: BillingOptionObject[]
  tenantAccountId: string
  /** Seeded from parent when already loaded for the account header. */
  billingStatus?: TenantAccountBillingStatusResponse
  accountStatus?: TenantAccountStatusResponse
  /** Derived Final flag from tenant DTO (CLOSED + settled); display only. */
  isFinalAccount?: boolean
  onBillingStatusUpdate?: (accountId: string, status: TenantAccountBillingStatusResponse) => void
  onAccountStatusUpdate?: (
    accountId: string,
    status: TenantAccountStatusResponse,
    options?: { refetchTenant?: boolean }
  ) => void
  onBillingConfigUpdate?: () => void
}

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

const renderBillingOptions = (
  billingOptions: BillingOptionObject[],
  billingOptionsMap?: Map<string, string>
): Array<{ id: string; name: string; description?: string }> =>
  billingOptions.map((option) => {
    const key = Object.keys(option)[0]
    const description = billingOptionsMap?.has(key) === true ? billingOptionsMap.get(key) : undefined
    return {
      id: key,
      name: option[key],
      ...(description !== undefined && { description }),
    }
  })

const cancellationReasonOptions = renderBillingOptions([
  { BILLING_CONFUSION_COMPLEXITY: 'Billing confusion - complexity' },
  { BILLING_CONFUSION_SAVINGS: 'Billing confusion - savings' },
  { BUSINESS_HAS_CLOSED: 'Business has closed' },
  { BUSINESS_IS_CLOSING: 'Business is closing' },
  { SAVINGS_TOO_LOW: 'Savings too low' },
])

const cancellationReasonLabelById = new Map(
  cancellationReasonOptions.map((option) => [option.id, option.name])
)

const TabBillingOptions: React.FC<TabBillingOptionsProps> = ({
  kingFormInitialValues,
  utilityFormInitialValues,
  utilityBillingOptions,
  kingBillingOptions,
  billingModeOptions,
  enrollmentOptions,
  tenantAccountId,
  billingStatus: billingStatusProp,
  accountStatus: accountStatusProp,
  isFinalAccount = false,
  onBillingStatusUpdate,
  onAccountStatusUpdate,
  onBillingConfigUpdate,
}) => {
  const dispatch = useAppDispatch()
  const { permissions } = useAppSelector((state) => state.app)
  const { billingOptions } = useAppSelector((state) => state.tenants)

  const { billType: utilityBillType, ccaProgram, demandCreditProgram } = utilityFormInitialValues
  const { billType: kingBillType, includeChart, includeSummary, energyDiscount, demandDiscount, billingMode } =
    kingFormInitialValues

  const hasModifyPermission = permissions?.includes('account:manager:billing-options:modify') ?? false
  const hasBillingStatusModifyPermission = permissions?.includes('account:manager:billing-options:modify') ?? false
  const hasAccountStatusModifyPermission = permissions?.includes('account:manager:status:modify') ?? false
  const hasModifyDiscountPermission = permissions?.includes('account:manager:billing-options:discounts:modify') ?? false
  const hasBillingModeModifyPermission = permissions?.includes('account:manager:billing-mode:modify') ?? false

  const [utilityBalanceAdjustment, setUtilityBalanceAdjustment] = useState('')
  const [invoiceBalanceAdjustment, setInvoiceBalanceAdjustment] = useState('')
  const [showBalanceFields, setShowBalanceFields] = useState(false)

  const [billingStatus, setBillingStatus] = useState<TenantAccountBillingStatusResponse | null>(billingStatusProp ?? null)
  const [loadingBillingStatus, setLoadingBillingStatus] = useState(billingStatusProp == null)
  const [isEditingBillingStatus, setIsEditingBillingStatus] = useState(false)
  const [accountStatus, setAccountStatus] = useState<TenantAccountStatusResponse | null>(accountStatusProp ?? null)
  const [loadingAccountStatus, setLoadingAccountStatus] = useState(accountStatusProp == null)
  const [isEditingAccountStatus, setIsEditingAccountStatus] = useState(false)
  const [isEditingBillingMode, setIsEditingBillingMode] = useState(false)
  const [isEditingUtilityBilling, setIsEditingUtilityBilling] = useState(false)
  const [isEditingKingBilling, setIsEditingKingBilling] = useState(false)
  const [showFullHistory, setShowFullHistory] = useState(false)
  const [ccFeePolicy, setCcFeePolicy] = useState<CcFeePolicyAdmin | null>(null)
  const [loadingCcFeePolicy, setLoadingCcFeePolicy] = useState(true)
  const [ccFeePolicyError, setCcFeePolicyError] = useState(false)

  useEffect(() => {
    if (billingStatusProp != null) {
      setBillingStatus(billingStatusProp)
      setLoadingBillingStatus(false)
    }
  }, [billingStatusProp])

  useEffect(() => {
    if (accountStatusProp == null) return
    // GET /tenant can seed status without notice fields; don't clobber a fuller local load.
    setAccountStatus((prev) => {
      if (
        prev != null &&
        prev.status === accountStatusProp.status &&
        (prev.cancellationNoticeDays != null || prev.pendingCloseStartedAt != null) &&
        accountStatusProp.cancellationNoticeDays == null &&
        accountStatusProp.pendingCloseStartedAt == null
      ) {
        return prev
      }
      return accountStatusProp
    })
    setLoadingAccountStatus(false)
  }, [accountStatusProp])

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      // Refresh in the background. Only show Loading when we have nothing seeded yet —
      // otherwise the panel flashes Loading → same content on every tab open.
      try {
        if (billingStatusProp == null) {
          setLoadingBillingStatus(true)
        }
        const status = await getTenantAccountBillingStatusApi(tenantAccountId)
        if (!cancelled) {
          setBillingStatus(status)
          onBillingStatusUpdate?.(tenantAccountId, status)
        }
      } catch (error) {
        console.error('Failed to fetch billing status:', error)
      } finally {
        if (!cancelled) setLoadingBillingStatus(false)
      }

      try {
        if (accountStatusProp == null) {
          setLoadingAccountStatus(true)
        }
        const status = await getTenantAccountStatusApi(tenantAccountId)
        if (!cancelled) {
          setAccountStatus(status)
          onAccountStatusUpdate?.(tenantAccountId, status)
        }
      } catch (error) {
        console.error('Failed to fetch account status:', error)
      } finally {
        if (!cancelled) setLoadingAccountStatus(false)
      }
    }

    void load()
    return () => {
      cancelled = true
    }
    // Intentionally omit status update callbacks — parent often passes inline callbacks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantAccountId])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoadingCcFeePolicy(true)
      setCcFeePolicyError(false)
      try {
        const policy = await getCcFeePolicyApi(tenantAccountId)
        if (!cancelled) {
          if (policy == null || typeof policy.charging !== 'boolean') {
            setCcFeePolicy(null)
            setCcFeePolicyError(true)
          } else {
            setCcFeePolicy(policy)
          }
        }
      } catch (error) {
        console.error('Failed to fetch card fee policy:', error)
        if (!cancelled) {
          setCcFeePolicy(null)
          setCcFeePolicyError(true)
        }
      } finally {
        if (!cancelled) setLoadingCcFeePolicy(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [tenantAccountId])

  const utilityForm = useFormik({
    initialValues: {
      billType: utilityBillType ?? 'STANDARD',
      ccaProgram: ccaProgram ?? 'NOT_ENROLLED',
      demandCreditProgram: demandCreditProgram ?? 'NOT_ENROLLED',
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      billType: Yup.string().required(),
      ccaProgram: Yup.string().required(),
      demandCreditProgram: Yup.string().required(),
    }),
    onSubmit: (values, { resetForm }) => {
      updateUtilityBillingConfigApi(values, tenantAccountId)
        .then(async () => {
          await dispatch(pushToast({ message: 'Utility billing configuration updated', type: 'success' }))
          setIsEditingUtilityBilling(false)
          resetForm({ values })
          onBillingConfigUpdate?.()
        })
        .catch(async (e) => {
          await dispatch(
            pushToast({
              message: 'Failed to update utility billing configuration',
              type: 'error',
              description: `${e.response?.data?.error !== undefined ? `${e.response?.data?.error as string}:` : 'Error:'} ${e.message as string}`,
            })
          )
        })
    },
  })

  const kingForm = useFormik({
    initialValues: {
      billType: kingBillType ?? 'STANDARD',
      includeChart: includeChart ?? false,
      includeSummary: includeSummary ?? false,
      energyDiscount: energyDiscount ?? 10,
      demandDiscount: demandDiscount ?? 0,
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      energyDiscount: Yup.number().min(0).max(100).required(),
      demandDiscount: Yup.number().min(0).max(100).required(),
      billType: Yup.string().required(),
      includeChart: Yup.boolean().required(),
      includeSummary: Yup.boolean().required(),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        const body = {
          ...values,
          billingMode: billingMode ?? 'CONSOLIDATED',
          energyDiscount: values.energyDiscount.toFixed(2),
          demandDiscount: values.demandDiscount.toFixed(2),
        }
        await updateKingBillingConfigApi(body, tenantAccountId)
        await dispatch(pushToast({ message: 'King billing configuration updated', type: 'success' }))
        setIsEditingKingBilling(false)
        resetForm({ values })
        onBillingConfigUpdate?.()
      } catch (e: any) {
        await dispatch(
          pushToast({
            message: 'Failed to update billing configuration',
            type: 'error',
            description: `${e.response?.data?.error !== undefined ? `${e.response?.data?.error as string}:` : 'Error:'} ${e.message as string}`,
          })
        )
      }
    },
  })

  const billingModeForm = useFormik({
    initialValues: {
      billingMode: billingMode ?? 'CONSOLIDATED',
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      billingMode: Yup.string().required(),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        await updateBillingModeApi(
          values.billingMode,
          tenantAccountId,
          utilityBalanceAdjustment,
          invoiceBalanceAdjustment
        )
        await dispatch(pushToast({ message: 'Billing mode updated', type: 'success' }))
        setUtilityBalanceAdjustment('')
        setInvoiceBalanceAdjustment('')
        setShowBalanceFields(false)
        setIsEditingBillingMode(false)
        resetForm({ values })
        onBillingConfigUpdate?.()
      } catch (e: any) {
        await dispatch(
          pushToast({
            message: 'Failed to update billing mode',
            type: 'error',
            description: `${e.response?.data?.error !== undefined ? `${e.response?.data?.error as string}:` : 'Error:'} ${e.message as string}`,
          })
        )
      }
    },
  })

  useEffect(() => {
    if (billingModeForm.values.billingMode !== billingModeForm.initialValues.billingMode) {
      setShowBalanceFields(true)
    } else {
      setShowBalanceFields(false)
      setUtilityBalanceAdjustment('')
      setInvoiceBalanceAdjustment('')
    }
  }, [billingModeForm.values.billingMode, billingModeForm.initialValues.billingMode])

  const billingStatusForm = useFormik({
    initialValues: {
      status: billingStatus?.current?.status || 'NONE',
      reasons: (billingStatus?.current?.reasons || []) as string[],
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      status: Yup.string().required('Status is required'),
      reasons: Yup.array().optional(),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        await updateTenantAccountBillingStatusApi(tenantAccountId, {
          billingStatus: values.status,
          reasons: values.reasons,
        })
        await dispatch(pushToast({ message: `Billing status updated to ${values.status}`, type: 'success' }))
        const updatedStatus = await getTenantAccountBillingStatusApi(tenantAccountId)
        setBillingStatus(updatedStatus)
        setIsEditingBillingStatus(false)
        resetForm()
        onBillingStatusUpdate?.(tenantAccountId, updatedStatus)
      } catch (error: any) {
        await dispatch(
          pushToast({
            message: 'Failed to update billing status',
            type: 'error',
            description: error.response?.data?.message || error.message,
          })
        )
      }
    },
  })

  const accountStatusForm = useFormik({
    initialValues: {
      status: accountStatus?.status || 'NONE',
      cancellationReason: accountStatus?.cancellationReason || '',
      cancellationNoticeDays:
        accountStatus?.cancellationNoticeDays != null ? String(accountStatus.cancellationNoticeDays) : '30',
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      status: Yup.string().required('Status is required'),
      cancellationReason: Yup.string().when('status', {
        is: 'PENDING_CLOSED',
        then: (schema) => schema.required('Cancelation reason is required'),
        otherwise: (schema) => schema.optional(),
      }),
      cancellationNoticeDays: Yup.string().when('status', {
        is: 'PENDING_CLOSED',
        then: (schema) =>
          schema
            .required('Notice days are required')
            .test('is-int', 'Notice days must be a number >= 0', (val) => {
              if (val == null || val === '') return false
              const n = Number(val)
              return Number.isInteger(n) && n >= 0
            }),
        otherwise: (schema) => schema.optional(),
      }),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        const payload: {
          status: string
          cancellationReason?: string
          cancellationNoticeDays?: number
        } = { status: values.status }
        if (values.status === 'PENDING_CLOSED') {
          payload.cancellationReason = values.cancellationReason
          payload.cancellationNoticeDays = Number(values.cancellationNoticeDays)
        }
        await updateTenantAccountStatusApi(tenantAccountId, payload)
        await dispatch(pushToast({ message: `Account status updated to ${values.status}`, type: 'success' }))
        const updatedStatus = await getTenantAccountStatusApi(tenantAccountId)
        setAccountStatus(updatedStatus)
        setIsEditingAccountStatus(false)
        resetForm()
        onAccountStatusUpdate?.(tenantAccountId, updatedStatus, { refetchTenant: true })
      } catch (error: any) {
        await dispatch(
          pushToast({
            message: 'Failed to update account status',
            type: 'error',
            description: error.response?.data?.message || error.message,
          })
        )
      }
    },
  })

  const showCancelationFields = accountStatusForm.values.status === 'PENDING_CLOSED'

  const closeBillingStatusModal = () => {
    setIsEditingBillingStatus(false)
    billingStatusForm.resetForm()
  }

  const closeAccountStatusModal = () => {
    setIsEditingAccountStatus(false)
    accountStatusForm.resetForm()
  }

  const closeBillingModeModal = () => {
    setIsEditingBillingMode(false)
    billingModeForm.resetForm()
    setShowBalanceFields(false)
    setUtilityBalanceAdjustment('')
    setInvoiceBalanceAdjustment('')
  }

  const closeUtilityBillingModal = () => {
    setIsEditingUtilityBilling(false)
    utilityForm.resetForm()
  }

  const closeKingBillingModal = () => {
    setIsEditingKingBilling(false)
    kingForm.resetForm()
  }

  const optionLabel = (options: BillingOptionObject[], key?: string): string => {
    if (key == null || key === '') return 'Not set'
    const match = options.find((option) => Object.keys(option)[0] === key)
    if (match != null) return match[key]
    return key
  }

  const SummaryField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div className="min-w-0 space-y-1">
      <span className="text-xs font-medium text-slate-500 dark:text-slate-400">{label}</span>
      <div className="text-sm text-slate-800 dark:text-slate-100">{children}</div>
    </div>
  )

  const billingStatusOptions = billingOptions?.billingStatusOptions
    ? renderBillingOptions(billingOptions.billingStatusOptions)
    : []
  const billingStatusReasonOptions = billingOptions?.billingStatusReasons
    ? renderBillingOptions(billingOptions.billingStatusReasons)
    : []
  const accountStatusOptions = billingOptions?.accountStatusOptions
    ? renderBillingOptions(billingOptions.accountStatusOptions)
    : []

  const renderSelectField = (
    name: keyof typeof utilityFormInitialValues | keyof typeof kingFormInitialValues | 'billingMode',
    label: string,
    options: BillingOptionObject[],
    description: string,
    formName: 'utilityForm' | 'kingForm' | 'billingModeForm',
    disabled: boolean,
    billingOptionsMap?: Map<string, string>,
    isEnrollmentSelect?: boolean
  ): JSX.Element => {
    const formikForm =
      formName === 'utilityForm' ? utilityForm : formName === 'billingModeForm' ? billingModeForm : kingForm
    const formValue =
      formName === 'utilityForm'
        ? utilityForm.values[name as keyof typeof utilityFormInitialValues]
        : formName === 'billingModeForm'
          ? billingModeForm.values.billingMode
          : kingForm.values[name as keyof typeof kingForm.values]

    return (
      <ComboboxAutocomplete
        data={renderBillingOptions(options, billingOptionsMap)}
        value={(formValue as string) ?? ''}
        handleChange={(value: string | undefined) => {
          formikForm.setFieldValue(name, value).catch(async (err) => {
            await dispatch(
              pushToast({
                message: 'Failed to update billing configuration',
                type: 'error',
                description: `${err.response?.data?.error !== undefined ? `${err.response?.data?.error as string}:` : 'Error:'} ${err.message as string}`,
              })
            )
          })
        }}
        label={label}
        description={isEnrollmentSelect !== true ? description : undefined}
        wildCardDisabled={true}
        disabled={disabled}
        isNotFilter={true}
        placeholder={isEnrollmentSelect === true ? 'Search for an enrollment type' : `Search for a ${label.toLowerCase()}...`}
      />
    )
  }

  const modeKey = isFinalAccount ? 'FINAL' : billingMode || ''
  const modeLabel = isFinalAccount
    ? 'Final'
    : billingModeOptionsDescriptionMap.get(modeKey) || modeKey || 'Not set'
  const modeImplication = isFinalAccount
    ? 'Account is closed with settled balances — display only; stored billing mode is unchanged'
    : billingModeImplicationMap.get(modeKey)

  const balanceFieldsValid =
    Boolean(utilityBalanceAdjustment) &&
    Boolean(invoiceBalanceAdjustment) &&
    !isNaN(parseFloat(utilityBalanceAdjustment)) &&
    !isNaN(parseFloat(invoiceBalanceAdjustment))

  const billingModeSaveDisabled =
    !billingModeForm.dirty || (showBalanceFields && !balanceFieldsValid)

  const kingSaveDisabled = !hasModifyPermission || !kingForm.dirty

  const history = billingStatus?.history ?? []
  const visibleHistory = showFullHistory ? history : history.slice(0, HISTORY_PREVIEW)

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Panel
          header={
            <PanelHeader
              title="Billing ops status"
              description="Operational billing status for this account"
              action={
                hasBillingStatusModifyPermission ? (
                  <EditPanelButton onClick={() => setIsEditingBillingStatus(true)} />
                ) : undefined
              }
            />
          }
        >
          {loadingBillingStatus ? (
            <span className="text-xs text-slate-400">Loading…</span>
          ) : billingStatus?.current ? (
            <div className="space-y-1.5">
              <div className="flex flex-wrap items-center gap-2">
                <Badge status={billingStatus.current.status} variant="soft" />
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  Effective {formatDate(billingStatus.current.effectiveDate)}
                </span>
              </div>
              {billingStatus.current.reasons.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {billingStatus.current.reasons.map((reason) => (
                    <Badge key={reason} status={reason} variant="soft" />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <span className="text-xs text-slate-400">Not available</span>
          )}
        </Panel>

        <Panel
          header={
            <PanelHeader
              title="KE account status"
              description="Whether this King Energy account is open or closed"
              action={
                hasAccountStatusModifyPermission ? (
                  <EditPanelButton onClick={() => setIsEditingAccountStatus(true)} />
                ) : undefined
              }
            />
          }
        >
          {loadingAccountStatus ? (
            <span className="text-xs text-slate-400">Loading…</span>
          ) : accountStatus ? (
            <div className="space-y-1">
              <Badge status={accountStatus.status} variant="soft" />
              {accountStatus.status === 'PENDING_CLOSED' &&
                accountStatus.cancellationNoticeDays != null &&
                accountStatus.pendingCloseStartedAt != null && (
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {(() => {
                      const started = new Date(accountStatus.pendingCloseStartedAt).getTime()
                      const elapsed = Math.floor((Date.now() - started) / (24 * 60 * 60 * 1000))
                      const remaining = Math.max(0, accountStatus.cancellationNoticeDays - elapsed)
                      return remaining === 0
                        ? 'Notice period elapsed — auto-close when balances allow'
                        : `${remaining} day${remaining === 1 ? '' : 's'} remaining in cancelation notice`
                    })()}
                    {accountStatus.cancellationReason != null
                      ? ` · ${
                          cancellationReasonLabelById.get(accountStatus.cancellationReason) ??
                          accountStatus.cancellationReason.replace(/_/g, ' ')
                        }`
                      : ''}
                  </p>
                )}
            </div>
          ) : (
            <span className="text-xs text-slate-400">Not available</span>
          )}
        </Panel>

        <Panel
          header={
            <PanelHeader
              title="Billing mode"
              description="How we calculate and present bills for this account"
              action={
                hasBillingModeModifyPermission ? (
                  <EditPanelButton onClick={() => setIsEditingBillingMode(true)} />
                ) : undefined
              }
            />
          }
        >
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              status={modeKey || 'UNKNOWN'}
              variant="soft"
              displayLabel={isFinalAccount ? 'Final' : undefined}
            />
            <span className="text-sm font-medium text-slate-800 dark:text-slate-100">{modeLabel}</span>
          </div>
          {modeImplication != null && (
            <p className="mt-1.5 text-xs leading-snug text-slate-500 dark:text-slate-400">{modeImplication}</p>
          )}
        </Panel>
      </div>

      <CustomContentModal isOpen={isEditingBillingStatus} onClose={closeBillingStatusModal}>
        <div className="w-full max-w-lg rounded-lg bg-white p-6 text-left shadow-xl dark:bg-slate-800">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Edit billing ops status</h3>
          <p className="mt-1 mb-4 text-sm text-gray-500 dark:text-gray-400">
            Update the operational billing status and reasons for this account.
          </p>
          <FormikProvider value={billingStatusForm}>
            <form onSubmit={billingStatusForm.handleSubmit} className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <ComboboxAutocomplete
                  data={billingStatusOptions}
                  value={billingStatusForm.values.status}
                  handleChange={(value: string | undefined) => {
                    billingStatusForm.setFieldValue('status', value)
                  }}
                  label="Billing Status"
                  description="Select new billing status"
                  wildCardDisabled={true}
                  disabled={false}
                  isNotFilter={true}
                  placeholder="Select status..."
                />
                <ComboboxAutocomplete
                  data={billingStatusReasonOptions}
                  value={''}
                  handleChange={(value: string | undefined) => {
                    const currentReasons = billingStatusForm.values.reasons || []
                    if (value && !currentReasons.includes(value)) {
                      billingStatusForm.setFieldValue('reasons', [...currentReasons, value])
                    }
                  }}
                  label="Reasons"
                  description="Add reason (multi-select)"
                  wildCardDisabled={true}
                  disabled={false}
                  isNotFilter={true}
                  placeholder="Add reason..."
                />
              </div>
              {billingStatusForm.values.reasons?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {billingStatusForm.values.reasons.map((reason: string) => (
                    <span
                      key={reason}
                      className="inline-flex items-center gap-1 rounded-md bg-slate-200/80 px-2 py-0.5 text-xs text-slate-700 dark:bg-slate-700 dark:text-slate-200"
                    >
                      {billingStatusReasonOptions.find((r) => r.id === reason)?.name || reason}
                      <button
                        type="button"
                        className="text-slate-500 hover:text-slate-800 dark:hover:text-slate-100"
                        onClick={() => {
                          billingStatusForm.setFieldValue(
                            'reasons',
                            billingStatusForm.values.reasons.filter((r: string) => r !== reason)
                          )
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" color="slate" onClick={closeBillingStatusModal}>
                  Cancel
                </Button>
                <Button type="submit" disabled={!billingStatusForm.isValid}>
                  Update status
                </Button>
              </div>
            </form>
          </FormikProvider>
        </div>
      </CustomContentModal>

      <CustomContentModal isOpen={isEditingAccountStatus} onClose={closeAccountStatusModal}>
        <div className="w-full max-w-md rounded-lg bg-white p-6 text-left shadow-xl dark:bg-slate-800">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Edit KE account status</h3>
          <p className="mt-1 mb-4 text-sm text-gray-500 dark:text-gray-400">
            Set whether this King Energy account is open, pending close, or closed. Pending Close requires a
            cancelation reason and notice period.
          </p>
          <FormikProvider value={accountStatusForm}>
            <form onSubmit={accountStatusForm.handleSubmit} className="space-y-3">
              <ComboboxAutocomplete
                data={accountStatusOptions}
                value={accountStatusForm.values.status}
                handleChange={(value: string | undefined) => {
                  accountStatusForm.setFieldValue('status', value)
                }}
                label="Account Status"
                description="Select new account status"
                wildCardDisabled={true}
                disabled={false}
                isNotFilter={true}
                placeholder="Select status..."
              />
              {showCancelationFields && (
                <>
                  <ComboboxAutocomplete
                    data={cancellationReasonOptions}
                    value={accountStatusForm.values.cancellationReason}
                    handleChange={(value: string | undefined) => {
                      accountStatusForm.setFieldValue('cancellationReason', value ?? '')
                    }}
                    label="Cancelation reason"
                    description="Why the account is pending close"
                    wildCardDisabled={true}
                    disabled={false}
                    isNotFilter={true}
                    placeholder="Select reason..."
                  />
                  <Input
                    type="number"
                    min={0}
                    value={accountStatusForm.values.cancellationNoticeDays}
                    onChange={(e) => accountStatusForm.setFieldValue('cancellationNoticeDays', e.target.value)}
                    label="Notice days"
                    description="Days before auto-close is eligible (balances must also clear)"
                    placeholder="30"
                    required
                  />
                </>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" color="slate" onClick={closeAccountStatusModal}>
                  Cancel
                </Button>
                <Button type="submit" disabled={!accountStatusForm.isValid}>
                  Update status
                </Button>
              </div>
            </form>
          </FormikProvider>
        </div>
      </CustomContentModal>

      <CustomContentModal isOpen={isEditingBillingMode} onClose={closeBillingModeModal}>
        <div className="w-full max-w-lg rounded-lg bg-white p-6 text-left shadow-xl dark:bg-slate-800">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Edit billing mode</h3>
          <p className="mt-1 mb-4 text-sm text-gray-500 dark:text-gray-400">
            Change how we calculate bills for this tenant. Mode changes may require balance adjustments.
          </p>
          <FormikProvider value={billingModeForm}>
            <form onSubmit={billingModeForm.handleSubmit} className="space-y-3">
              {renderSelectField(
                'billingMode',
                'Billing Mode',
                billingModeOptions,
                'How we calculate bills for the tenant',
                'billingModeForm',
                false,
                billingModeOptionsDescriptionMap
              )}
              {showBalanceFields && (
                <div className="rounded-md border border-amber-300/80 bg-amber-50/80 p-3 dark:border-amber-500/40 dark:bg-amber-950/30">
                  <p className="mb-2 text-sm font-medium text-amber-900 dark:text-amber-200">
                    Billing mode change requires balance adjustments
                  </p>
                  <p className="mb-3 text-xs text-amber-800/90 dark:text-amber-300/90">
                    Enter both amounts before saving. Leaving either blank will block save.
                  </p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Input
                      type="number"
                      step="0.01"
                      value={utilityBalanceAdjustment}
                      onChange={(e) => setUtilityBalanceAdjustment(e.target.value)}
                      label="Utility bill balance adjustment"
                      description="Utility bill balance adjustment amount"
                      placeholder="0.00"
                      required
                    />
                    <Input
                      type="number"
                      step="0.01"
                      value={invoiceBalanceAdjustment}
                      onChange={(e) => setInvoiceBalanceAdjustment(e.target.value)}
                      label="Invoice balance adjustment"
                      description="Invoice balance adjustment amount"
                      placeholder="0.00"
                      required
                    />
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" color="slate" onClick={closeBillingModeModal}>
                  Cancel
                </Button>
                <Button type="submit" disabled={billingModeSaveDisabled}>
                  Update billing mode
                </Button>
              </div>
            </form>
          </FormikProvider>
        </div>
      </CustomContentModal>

      {/* Config panels */}
      <div className="grid gap-4 md:grid-cols-2">
        <Panel
          header={
            <PanelHeader
              title="Utility billing"
              description="Bill access and program enrollment"
              action={
                hasModifyPermission ? (
                  <EditPanelButton onClick={() => setIsEditingUtilityBilling(true)} />
                ) : undefined
              }
            />
          }
        >
          <div className="grid gap-3 sm:grid-cols-3">
            <SummaryField label="Bill type">
              <span className="font-medium">{optionLabel(utilityBillingOptions, utilityBillType)}</span>
              {utilityBillType != null && utilityBillTypeDescriptionMap.has(utilityBillType) && (
                <p className="mt-0.5 text-xs font-normal leading-snug text-slate-500 dark:text-slate-400">
                  {utilityBillTypeDescriptionMap.get(utilityBillType)}
                </p>
              )}
            </SummaryField>
            <SummaryField label="CCA program">
              <Badge status={ccaProgram ?? 'NOT_ENROLLED'} variant="soft" />
            </SummaryField>
            <SummaryField label="Demand credit program">
              <Badge status={demandCreditProgram ?? 'NOT_ENROLLED'} variant="soft" />
            </SummaryField>
          </div>
        </Panel>

        <Panel
          header={
            <PanelHeader
              title="King billing"
              description="Invoice shape and discounts"
              action={
                hasModifyPermission || hasModifyDiscountPermission ? (
                  <EditPanelButton onClick={() => setIsEditingKingBilling(true)} />
                ) : undefined
              }
            />
          }
        >
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <SummaryField label="Bill type">
              <span className="font-medium">{optionLabel(kingBillingOptions, kingBillType)}</span>
            </SummaryField>
            <SummaryField label="Energy pie chart">
              <Badge status={includeChart ? 'YES' : 'NO'} variant="soft" />
            </SummaryField>
            <SummaryField label="Detailed summary">
              <Badge status={includeSummary ? 'YES' : 'NO'} variant="soft" />
            </SummaryField>
            <SummaryField label="Energy discount">
              <span className="font-medium">{energyDiscount ?? 0}%</span>
            </SummaryField>
            <SummaryField label="Demand discount">
              <span className="font-medium">{demandDiscount ?? 0}%</span>
            </SummaryField>
          </div>
        </Panel>
      </div>

      <Panel
        header={
          <PanelHeader
            title="Card processing fee"
            description={
              loadingCcFeePolicy
                ? 'Loading card fee policy'
                : ccFeePolicyError
                  ? 'Unable to load fee policy'
                  : ccFeePanelDescription()
            }
          />
        }
        data-testid="cc-fee-policy-panel"
      >
        {loadingCcFeePolicy ? (
          <span className="text-xs text-slate-400">Loading</span>
        ) : ccFeePolicyError ? (
          <span className="text-xs text-slate-400">Unable to load fee policy</span>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <SummaryField label="Charging">
              <Badge
                status={ccFeeChargingStatus(ccFeePolicy?.charging === true)}
                displayLabel={ccFeePolicy?.charging === true ? 'Charging' : 'Not charging'}
                variant="soft"
              />
              <p className="mt-0.5 text-xs font-normal leading-snug text-slate-500 dark:text-slate-400">
                {ccFeeChargingHint(ccFeePolicy)}
              </p>
            </SummaryField>
            <SummaryField label="Source">
              <span className="font-medium">{ccFeeSourceLabel(ccFeePolicy?.source, ccFeePolicy?.tesaVersion)}</span>
            </SummaryField>
            <SummaryField label="Mode">
              <span className="font-medium">{ccFeeModeLabel(ccFeePolicy?.mode)}</span>
            </SummaryField>
            <SummaryField label="Rate">
              <span className="font-medium">{ccFeeAmountLabel(ccFeePolicy)}</span>
            </SummaryField>
            <SummaryField label="Monthly cap">
              <span className="font-medium">{ccFeeCapLabel(ccFeePolicy?.monthlyCapAmount)}</span>
            </SummaryField>
            <SummaryField label="Start date">
              <span className="font-medium">{ccFeeDateLabel(ccFeePolicy?.startDate)}</span>
            </SummaryField>
            <SummaryField label="End date">
              <span className="font-medium">{ccFeeDateLabel(ccFeePolicy?.endDate)}</span>
            </SummaryField>
            {ccFeePolicy?.reason != null && ccFeePolicy.reason !== '' && (
              <SummaryField label="Reason">
                <span className="font-medium">{ccFeePolicy.reason}</span>
              </SummaryField>
            )}
            <SummaryField label="TESA schedule">
              <span className="font-medium">{ccFeeScheduleLabel(ccFeePolicy)}</span>
            </SummaryField>
            <SummaryField label="Pass-through rate">
              <span className="font-medium">{ccFeePassThroughLabel(ccFeePolicy)}</span>
            </SummaryField>
          </div>
        )}
      </Panel>

      <CustomContentModal isOpen={isEditingUtilityBilling} onClose={closeUtilityBillingModal}>
        <div className="w-full max-w-lg rounded-lg bg-white p-6 text-left shadow-xl dark:bg-slate-800">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Edit utility billing</h3>
          <p className="mt-1 mb-4 text-sm text-gray-500 dark:text-gray-400">
            Bill access and program enrollment for this account.
          </p>
          <FormikProvider value={utilityForm}>
            <form onSubmit={utilityForm.handleSubmit} className="space-y-3">
              {renderSelectField(
                'billType',
                'Bill Type',
                utilityBillingOptions,
                'Utility bill type(s) available to the tenant',
                'utilityForm',
                !hasModifyPermission,
                utilityBillTypeDescriptionMap
              )}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {renderSelectField(
                  'ccaProgram',
                  'CCA Program',
                  enrollmentOptions,
                  'CCA Program',
                  'utilityForm',
                  !hasModifyPermission,
                  undefined,
                  true
                )}
                {renderSelectField(
                  'demandCreditProgram',
                  'Demand Credit Program',
                  enrollmentOptions,
                  'Demand Credit Program',
                  'utilityForm',
                  !hasModifyPermission,
                  undefined,
                  true
                )}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" color="slate" onClick={closeUtilityBillingModal}>
                  Cancel
                </Button>
                <Button type="submit" disabled={!hasModifyPermission || !utilityForm.dirty}>
                  Save changes
                </Button>
              </div>
            </form>
          </FormikProvider>
        </div>
      </CustomContentModal>

      <CustomContentModal isOpen={isEditingKingBilling} onClose={closeKingBillingModal}>
        <div className="w-full max-w-lg rounded-lg bg-white p-6 text-left shadow-xl dark:bg-slate-800">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Edit King billing</h3>
          <p className="mt-1 mb-4 text-sm text-gray-500 dark:text-gray-400">
            Invoice shape and contracted discounts for this account.
          </p>
          <FormikProvider value={kingForm}>
            <form onSubmit={kingForm.handleSubmit} className="space-y-3">
              {renderSelectField(
                'billType',
                'Bill Type',
                kingBillingOptions,
                'Type of KE bill generated',
                'kingForm',
                !hasModifyPermission
              )}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <FormikToggle name="includeChart" label="Display Energy Pie Chart" disabled={!hasModifyPermission} />
                <FormikToggle name="includeSummary" label="Display Detailed Summary" disabled={!hasModifyPermission} />
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field
                  as={Input}
                  type="number"
                  name="energyDiscount"
                  label="Energy Discount"
                  error={kingForm.errors.energyDiscount}
                  disabled={!hasModifyDiscountPermission}
                  description="K.E. contracted discount"
                />
                <Field
                  as={Input}
                  type="number"
                  name="demandDiscount"
                  label="Demand Discount"
                  error={kingForm.errors.demandDiscount}
                  disabled={!hasModifyDiscountPermission}
                  description="% of demand savings passed onto tenant"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" color="slate" onClick={closeKingBillingModal}>
                  Cancel
                </Button>
                <Button type="submit" disabled={kingSaveDisabled}>
                  Save changes
                </Button>
              </div>
            </form>
          </FormikProvider>
        </div>
      </CustomContentModal>

      <Panel header={<PanelHeader title="Billing ops status history" />}>
        {loadingBillingStatus ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : history.length > 0 ? (
          <div className="space-y-3">
            {visibleHistory.map((entry) => (
              <div key={entry.id} className="flex items-start gap-3 text-xs">
                <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-slate-300 dark:bg-slate-600" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge status={entry.status} variant="soft" />
                    <span className="text-slate-500 dark:text-slate-400">{formatDate(entry.effectiveDate)}</span>
                    {entry.modifiedBy && (
                      <span className="text-slate-400">
                        by {entry.modifiedBy === 'System' ? entry.createdBy : entry.modifiedBy}
                      </span>
                    )}
                  </div>
                  {entry.reasons.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {entry.reasons.map((reason) => (
                        <Badge key={`${entry.id}-${reason}`} status={reason} variant="soft" />
                      ))}
                    </div>
                  )}
                  {entry.priorStatus && (
                    <div className="mt-1 text-slate-500">
                      Changed from: {entry.priorStatus}
                      {entry.priorReasons.length > 0 && ` (${entry.priorReasons.join(', ')})`}
                    </div>
                  )}
                  {entry.modifiedDescription && (
                    <div className="mt-1 text-slate-600 dark:text-slate-300">{entry.modifiedDescription}</div>
                  )}
                </div>
              </div>
            ))}
            {history.length > HISTORY_PREVIEW && (
              <button
                type="button"
                className="text-xs font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400"
                onClick={() => setShowFullHistory((v) => !v)}
              >
                {showFullHistory
                  ? 'Show less'
                  : `Show ${history.length - HISTORY_PREVIEW} older`}
              </button>
            )}
          </div>
        ) : (
          <p className="text-xs text-slate-500">No status history available</p>
        )}
      </Panel>
    </div>
  )
}

export default TabBillingOptions
